import type { SearchRequest } from '@vagusx/shared';

/**
 * Centralized query keys for TanStack Query.
 * Using factory pattern for type-safe, consistent key generation.
 */
export const queryKeys = {
  // User queries
  users: {
    all: ['users'] as const,
    me: () => [...queryKeys.users.all, 'me'] as const,
    mePermissions: () => [...queryKeys.users.all, 'me', 'permissions'] as const,
    list: (filters?: SearchRequest) => [...queryKeys.users.all, 'list', filters] as const,
    inactive: () => [...queryKeys.users.all, 'inactive'] as const,
    detail: (id: string) => [...queryKeys.users.all, 'detail', id] as const,
  },

  // User UI preferences (per-user, cross-device via server)
  userPreferences: {
    all: ['user-preferences'] as const,
  },

  // Organization queries
  organizations: {
    all: ['organizations'] as const,
    current: () => [...queryKeys.organizations.all, 'current'] as const,
    domains: () => [...queryKeys.organizations.all, 'domains'] as const,
  },

  // Invitation queries
  invitations: {
    all: ['invitations'] as const,
    list: () => [...queryKeys.invitations.all, 'list'] as const,
    pending: () => [...queryKeys.invitations.all, 'pending'] as const,
    byToken: (token: string) => [...queryKeys.invitations.all, 'token', token] as const,
  },

  // Patient queries
  patients: {
    all: ['patients'] as const,
    list: (filters?: SearchRequest) => [...queryKeys.patients.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.patients.all, 'detail', id] as const,
  },

  // Provider queries
  providers: {
    all: ['providers'] as const,
    list: () => [...queryKeys.providers.all, 'list'] as const,
    /**
     * Entity-picker catalog. SEPARATE from `list()` on purpose: the picker caches
     * `{ data, meta }` while `list()` caches the raw `SearchResponse`. Sharing one
     * key let whichever query resolved first decide the shape, and the picker read
     * `.data` off a `SearchResponse` and rendered an empty dropdown. Still under
     * `all`, so existing prefix invalidations keep covering it.
     */
    picker: () => [...queryKeys.providers.all, 'picker'] as const,
    inactive: () => [...queryKeys.providers.all, 'inactive'] as const,
    detail: (id: string) => [...queryKeys.providers.all, 'detail', id] as const,
    locations: (providerId: string) =>
      [...queryKeys.providers.all, 'detail', providerId, 'locations'] as const,
  },

  // Location queries
  locations: {
    all: ['locations'] as const,
    list: () => [...queryKeys.locations.all, 'list'] as const,
    /** Entity-picker catalog — see {@link queryKeys.providers.picker}. */
    picker: () => [...queryKeys.locations.all, 'picker'] as const,
    /** The picker catalog narrowed to locations a patient can be registered under. */
    registrationPicker: () => [...queryKeys.locations.all, 'picker', 'registration'] as const,
    detail: (id: string) => [...queryKeys.locations.all, 'detail', id] as const,
    providers: (locationId: string) =>
      [...queryKeys.locations.all, 'detail', locationId, 'providers'] as const,
  },

  // Task queries
  tasks: {
    all: ['tasks'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.tasks.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.tasks.all, 'detail', id] as const,
    history: (taskId: string) => [...queryKeys.tasks.all, 'detail', taskId, 'history'] as const,
    inbox: (filters?: Record<string, unknown>) =>
      [...queryKeys.tasks.all, 'inbox', filters] as const,
    counts: (filters?: Record<string, unknown>) =>
      [...queryKeys.tasks.all, 'counts', filters] as const,
    timeline: (taskId: string) => [...queryKeys.tasks.all, 'detail', taskId, 'timeline'] as const,
    related: (taskId: string) => [...queryKeys.tasks.all, 'detail', taskId, 'related'] as const,
    notes: (taskId: string) => [...queryKeys.tasks.all, 'detail', taskId, 'notes'] as const,
    summary: (taskId: string) => [...queryKeys.tasks.all, 'detail', taskId, 'summary'] as const,
  },

  // Email queries
  emails: {
    all: ['emails'] as const,
    detail: (id: string) => [...queryKeys.emails.all, 'detail', id] as const,
    attachments: (emailId: string) => [...queryKeys.emails.all, 'attachments', emailId] as const,
  },

  // Interaction queries
  interactions: {
    all: ['interactions'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.interactions.all, 'list', filters] as const,
    live: () => [...queryKeys.interactions.all, 'live'] as const,
    liveCount: () => [...queryKeys.interactions.all, 'live-count'] as const,
    detail: (id: string) => [...queryKeys.interactions.all, 'detail', id] as const,
    messages: (interactionId: string) =>
      [...queryKeys.interactions.all, 'detail', interactionId, 'messages'] as const,
    timeline: (interactionId: string) =>
      [...queryKeys.interactions.all, 'detail', interactionId, 'timeline'] as const,
    recordingUrl: (interactionId: string) =>
      [...queryKeys.interactions.all, 'detail', interactionId, 'recording-url'] as const,
    evaluation: (interactionId: string) =>
      [...queryKeys.interactions.all, 'detail', interactionId, 'evaluation'] as const,
    patientTimeline: (patientId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.interactions.all, 'patient', patientId, filters] as const,
  },

  // Team queries
  teams: {
    all: ['teams'] as const,
    list: () => [...queryKeys.teams.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.teams.all, 'detail', id] as const,
  },

  // Agent queries
  agents: {
    all: ['agents'] as const,
    list: () => [...queryKeys.agents.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.agents.all, 'detail', id] as const,
  },

  // Integration queries
  integrations: {
    all: ['integrations'] as const,
    list: () => [...queryKeys.integrations.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.integrations.all, 'detail', id] as const,
    ehrActivity: () => [...queryKeys.integrations.all, 'ehr-activity'] as const,
    streams: (id: string) => [...queryKeys.integrations.all, 'streams', id] as const,
    subscriptions: (id: string) => [...queryKeys.integrations.all, 'subscriptions', id] as const,
  },

  // Role queries
  roles: {
    all: ['roles'] as const,
    list: () => [...queryKeys.roles.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.roles.all, 'detail', id] as const,
    permissions: () => [...queryKeys.roles.all, 'permissions'] as const,
  },

  // Passkey queries
  passkeys: {
    all: ['passkeys'] as const,
    list: () => [...queryKeys.passkeys.all, 'list'] as const,
    /** Route guard's "does this user have any passkey" answer, per user. */
    exists: (userId: string) => [...queryKeys.passkeys.all, 'exists', userId] as const,
  },

  // BetterAuth session, as read by the route guards
  auth: {
    all: ['auth'] as const,
    session: () => [...queryKeys.auth.all, 'session'] as const,
  },

  // Entity queries
  entities: {
    all: ['entities'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.entities.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.entities.all, 'detail', id] as const,
    contacts: (entityId: string) =>
      [...queryKeys.entities.all, 'detail', entityId, 'contacts'] as const,
  },

  // Document extraction queries
  documents: {
    all: ['documents'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.documents.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.documents.all, 'detail', id] as const,
  },

  // Appointment queries
  appointments: {
    all: ['appointments'] as const,
    // The limit is part of the key: callers ask for different page sizes, and a
    // shorter cached page must not stand in for a longer one.
    upcoming: (patientId: string, limit?: number) =>
      [...queryKeys.appointments.all, 'upcoming', patientId, limit] as const,
    byPatient: (patientId: string) =>
      [...queryKeys.appointments.all, 'patient', patientId] as const,
    detail: (id: string) => [...queryKeys.appointments.all, 'detail', id] as const,
    schedule: (filters?: Record<string, unknown>) =>
      [...queryKeys.appointments.all, 'schedule', filters] as const,
    daySlots: (filters?: Record<string, unknown>) =>
      [...queryKeys.appointments.all, 'day-slots', filters] as const,
    offerableSlots: (filters?: Record<string, unknown>) =>
      [...queryKeys.appointments.all, 'offerable-slots', filters] as const,
    rescheduleSlots: (id: string | undefined) =>
      [...queryKeys.appointments.all, 'reschedule-slots', id] as const,
  },

  // Waitlist (ADR-271)
  waitlist: {
    all: ['waitlist'] as const,
    /** Every waitlist list, whatever its filters (for invalidation). */
    lists: () => [...queryKeys.waitlist.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.waitlist.lists(), filters] as const,
    detail: (id: string) => [...queryKeys.waitlist.all, 'detail', id] as const,
  },

  // Referrals quick-view (referral-labeled tasks)
  referrals: {
    all: ['referrals'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.referrals.all, 'list', filters] as const,
  },

  // Schedule block queries
  scheduleBlocks: {
    all: ['scheduleBlocks'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.scheduleBlocks.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.scheduleBlocks.all, 'detail', id] as const,
  },

  // Classification label queries
  classificationLabels: {
    all: ['classificationLabels'] as const,
    list: () => [...queryKeys.classificationLabels.all, 'list'] as const,
  },

  // Search queries
  search: {
    all: ['search'] as const,
    query: (q: string) => [...queryKeys.search.all, q] as const,
  },

  // Assignment rule queries
  assignmentRules: {
    all: ['assignmentRules'] as const,
    list: () => [...queryKeys.assignmentRules.all, 'list'] as const,
    catchAll: () => [...queryKeys.assignmentRules.all, 'catch-all'] as const,
    detail: (id: string) => [...queryKeys.assignmentRules.all, 'detail', id] as const,
  },

  // Appointment type queries
  appointmentTypes: {
    all: ['appointmentTypes'] as const,
    list: () => [...queryKeys.appointmentTypes.all, 'list'] as const,
    /** Entity-picker catalog — see {@link queryKeys.providers.picker}. */
    picker: () => [...queryKeys.appointmentTypes.all, 'picker'] as const,
    detail: (id: string) => [...queryKeys.appointmentTypes.all, 'detail', id] as const,
    overrides: (appointmentTypeId: string) =>
      [...queryKeys.appointmentTypes.all, 'detail', appointmentTypeId, 'overrides'] as const,
    overridesByProvider: (providerId: string, locationId?: string) =>
      [...queryKeys.appointmentTypes.all, 'overrides', 'provider', providerId, locationId] as const,
  },

  // Payor queries
  payors: {
    all: ['payors'] as const,
    catalog: () => [...queryKeys.payors.all, 'catalog'] as const,
    search: (query: string) => [...queryKeys.payors.all, 'search', query] as const,
    plans: (payorId: string) => [...queryKeys.payors.all, 'plans', payorId] as const,
    accepted: () => [...queryKeys.payors.all, 'accepted'] as const,
  },

  // Audit log queries
  auditLog: {
    all: ['auditLog'] as const,
    forObject: (objectType: string, objectId: string) =>
      [...queryKeys.auditLog.all, objectType, objectId] as const,
  },

  // Appointment type matching queries
  appointmentTypeMatching: {
    all: ['appointmentTypeMatching'] as const,
    preferences: () => [...queryKeys.appointmentTypeMatching.all, 'preferences'] as const,
    forTask: (taskId: string) =>
      [...queryKeys.appointmentTypeMatching.all, 'for-task', taskId] as const,
    // Keyed on the joined task ids: one cache entry per PAGE of the Referrals
    // list, so paging back and forth reuses the batch instead of re-ranking.
    providersForTasks: (taskIds: string[]) =>
      [...queryKeys.appointmentTypeMatching.all, 'providers-for-tasks', taskIds.join(',')] as const,
    // The same referral re-matched against ONE (provider, location) pair.
    // `signalsKey` is part of the key because the same pair gives a different
    // answer for a different referral.
    scopedToProvider: (providerId: string, locationId: string, signalsKey: string) =>
      [
        ...queryKeys.appointmentTypeMatching.all,
        'scoped',
        providerId,
        locationId,
        signalsKey,
      ] as const,
  },

  // Provider selection (ranking weights + strategy mix)
  providerSelection: {
    all: ['providerSelection'] as const,
    settings: () => [...queryKeys.providerSelection.all, 'settings'] as const,
  },

  // Dashboard queries
  dashboard: {
    all: ['dashboard'] as const,
    stats: (metrics: readonly string[], from?: string, to?: string) =>
      [
        ...queryKeys.dashboard.all,
        'stats',
        [...metrics].sort().join(','),
        from ?? null,
        to ?? null,
      ] as const,
  },

  // Outreach campaigns (ADR-154)
  outreach: {
    all: ['outreach'] as const,
    campaigns: () => [...queryKeys.outreach.all, 'campaigns'] as const,
    campaign: (id: string) => [...queryKeys.outreach.all, 'campaign', id] as const,
    versions: (id: string) => [...queryKeys.outreach.all, 'campaign', id, 'versions'] as const,
    catalog: () => [...queryKeys.outreach.all, 'audience-catalog'] as const,
    contactPolicy: () => [...queryKeys.outreach.all, 'contact-policy'] as const,
    // Preview and simulation are keyed on the DOCUMENT, not the campaign: the
    // editor asks about a spec that is not saved anywhere yet, and two campaigns
    // with the same audience should share the answer.
    preview: (audienceKey: string) => [...queryKeys.outreach.all, 'preview', audienceKey] as const,
    simulation: (specKey: string, outcome: string, locationId: string | null) =>
      [...queryKeys.outreach.all, 'simulate', specKey, outcome, locationId] as const,
    // ADR-318, F/G: the workflows a step may run, and the launch check on a spec.
    workflowOptions: () => [...queryKeys.outreach.all, 'workflow-options'] as const,
    launchCheck: (specKey: string) => [...queryKeys.outreach.all, 'launch-check', specKey] as const,
    // ADR-318, H: a campaign's queue and results.
    queueSummary: (id: string) =>
      [...queryKeys.outreach.all, 'campaign', id, 'queue-summary'] as const,
    queue: (id: string, lane: string) =>
      [...queryKeys.outreach.all, 'campaign', id, 'queue', lane] as const,
    results: (id: string, from: string, to: string) =>
      [...queryKeys.outreach.all, 'campaign', id, 'results', from, to] as const,
  },

  // Workflow builder (ADR-275)
  workflowDefns: {
    all: ['workflow-defns'] as const,
    catalog: () => [...queryKeys.workflowDefns.all, 'catalog'] as const,
    list: () => [...queryKeys.workflowDefns.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.workflowDefns.all, 'detail', id] as const,
    testInputs: (id: string, nodeId: string | null, testKey: number) =>
      [...queryKeys.workflowDefns.all, 'test-inputs', id, nodeId ?? 'whole', testKey] as const,
    testResult: (id: string, interactionId: string) =>
      [...queryKeys.workflowDefns.all, 'test-result', id, interactionId] as const,
    testCapture: (id: string, interactionId: string) =>
      [...queryKeys.workflowDefns.all, 'test-capture', id, interactionId] as const,
  },

  // Who may add a test appointment from a referral (ADR-293)
  testBooking: {
    access: () => ['test-booking', 'access'] as const,
  },

  // Platform (cross-org impersonation) queries
  platform: {
    all: ['platform'] as const,
    whoami: () => [...queryKeys.platform.all, 'whoami'] as const,
    config: () => [...queryKeys.platform.all, 'config'] as const,
    organizations: (filters?: { q?: string; page?: number; pageSize?: number }) =>
      [...queryKeys.platform.all, 'organizations', filters] as const,
    orgUsers: (orgId: string) => [...queryKeys.platform.all, 'orgs', orgId, 'users'] as const,
    adminUsers: (status: 'active' | 'inactive' = 'active') =>
      [...queryKeys.platform.all, 'admin-users', status] as const,
  },
} as const;
